import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, Users, Eye, Globe, Loader2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-gray-400 text-xs">{label}</p>
      <p className="text-white font-bold text-xl tabular-nums">{value}</p>
    </div>
  </div>
);

const AnalyticsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (d) => {
    const parts = d.split('-');
    return `${parts[2]}/${parts[1]}`;
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="analytics-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-white font-bold text-base md:text-lg">Analytics Visiteurs</h2>
            <p className="text-gray-400 text-xs">Trafic du site (données internes, stockées dans MongoDB)</p>
          </div>
        </div>
        <div className="flex gap-1.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d} onClick={() => setDays(d)}
              data-testid={`analytics-range-${d}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${days === d ? 'bg-purple-600 text-white' : 'bg-zinc-900 text-gray-400 hover:text-white'}`}
            >
              {d}j
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 text-purple-500 animate-spin mx-auto" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <StatCard icon={Eye} label="Pages vues" value={data.total_views} color="bg-purple-500/15 text-purple-400" />
            <StatCard icon={Users} label="Visiteurs uniques" value={data.total_visitors} color="bg-cyan-500/15 text-cyan-400" />
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <h3 className="text-white font-semibold text-sm mb-4">Visites par jour</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a855f7" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="visitorsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fill: '#71717a', fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: '#71717a', fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: '8px', fontSize: '12px' }}
                    labelStyle={{ color: '#fff' }}
                    labelFormatter={formatDate}
                  />
                  <Area type="monotone" dataKey="views" name="Pages vues" stroke="#a855f7" strokeWidth={2} fill="url(#viewsGrad)" />
                  <Area type="monotone" dataKey="visitors" name="Visiteurs" stroke="#06b6d4" strokeWidth={2} fill="url(#visitorsGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h3 className="text-white font-semibold text-sm mb-3">Pages les plus vues</h3>
              {data.top_pages.length === 0 ? (
                <p className="text-gray-500 text-xs">Aucune donnée</p>
              ) : (
                <div className="space-y-2">
                  {data.top_pages.map((p, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <span className="text-gray-300 text-xs truncate font-mono">{p.page}</span>
                      <span className="text-purple-400 text-xs font-bold tabular-nums flex-shrink-0">{p.views}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-400" /> Sources de trafic</h3>
              {data.top_referrers.length === 0 ? (
                <p className="text-gray-500 text-xs">Accès directs uniquement pour l'instant</p>
              ) : (
                <div className="space-y-2">
                  {data.top_referrers.map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <span className="text-gray-300 text-xs truncate">{r.referrer}</span>
                      <span className="text-cyan-400 text-xs font-bold tabular-nums flex-shrink-0">{r.views}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsTab;
